import type {
  PaginatedResponse,
  Account,
  Folder,
  Entry,
  PaginatedSearchResponse,
  AllSearchResults,
  AccountSearchResults,
  Classification,
  Profile,
  ProfileType
} from './types/cit-public-api';

import { ENTRIES_PER_PAGE } from './constants';
import HTTPError from './errors/http-error';
import NotFoundError from './errors/notfound-error';
import { getLogger } from './logger';

// TODO: change permenantly to public api address when made public
const API_URL = process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL;

// TODO: remove when responses are made the same (possibly never due to legacy use of public api)
// overloads to ensure presence of search param makes return type PaginatedSearchResponse
// critical because PaginatedSearchResponse names its entry responses "entries" and PaginatedResponse is "results"
export async function getEntriesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor?: string | undefined
): Promise<PaginatedResponse<Entry> | undefined>;

export async function getEntriesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor: string | undefined,
  search: string
): Promise<PaginatedSearchResponse<Entry> | undefined>;

// Get all entries by account paginated
export async function getEntriesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor?: string | undefined,
  searchQuery?: string
): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse<Entry> | undefined> {
  const params = new URLSearchParams();

  if (cursor) {
    params.append('cursor', cursor);
  }
  if (searchQuery) {
    params.append('query', searchQuery);
  }
  params.append('size', size.toString());

  const url = `${API_URL}/accounts/${accountId}/entries${
    searchQuery ? '/search' : ''
  }?${params.toString()}`;

  const logger = getLogger(citOrigin);
  logger.info({
    event: 'getEntriesByAccountPaginated',
    url,
    accountId,
    size,
    citOrigin,
    searchQuery,
    cursor
  });

  const response = await fetch(url, {
    headers: {
      'x-cit-origin': citOrigin
    }
  });

  if (response.status === 404) {
    logger.error({
      event: 'API ERROR: getEntriesByAccountPaginated',
      responseStatusText: response.statusText,
      url,
      accountId,
      size,
      citOrigin,
      searchQuery,
      cursor,
      statusCode: response.status
    });
    throw new NotFoundError(`Entries by account not found: ${accountId}`);
  }

  if (!response.ok) {
    logger.error({
      event: 'API ERROR: getEntriesByAccountPaginated',
      responseStatusText: response.statusText,
      url,
      accountId,
      size,
      citOrigin,
      searchQuery,
      cursor,
      statusCode: response.status
    });
    throw new HTTPError(
      response.status,
      `Failed to fetch entries by account: ${response.statusText}`
    );
  }
  const responseData = (await response.json()) as
    | PaginatedResponse<Entry>
    | PaginatedSearchResponse<Entry>;

  return responseData;
}

// TODO: remove when responses are made the same (possibly never due to legacy use of public api)
// overloads to ensure presence of search param makes return type PaginatedSearchResponse
// critical because PaginatedSearchResponse names its entry responses "entries" and PaginatedResponse is "results"
export async function getProfilesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor?: string | undefined
): Promise<PaginatedResponse<Profile> | undefined>;

export async function getProfilesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor: string | undefined,
  search: string
): Promise<PaginatedSearchResponse<Profile> | undefined>;

// Get all profiles by account paginated
export async function getProfilesByAccountPaginated(
  accountId: string,
  size: number,
  citOrigin: string,
  cursor?: string | undefined,
  searchQuery?: string
): Promise<PaginatedResponse<Profile> | PaginatedSearchResponse<Profile> | undefined> {
  const params = new URLSearchParams();

  if (cursor) {
    params.append('cursor', cursor);
  }
  if (searchQuery) {
    params.append('query', searchQuery);
  }
  params.append('size', size.toString());

  const url = `${API_URL}/accounts/${accountId}/profiles/instances${
    searchQuery ? '/search' : ''
  }?${params.toString()}`;

  const logger = getLogger(citOrigin);
  logger.info({
    event: 'getProfilesByAccountPaginated',
    url,
    accountId,
    size,
    citOrigin,
    searchQuery,
    cursor
  });

  const response = await fetch(url, {
    headers: {
      'x-cit-origin': citOrigin
    }
  });

  if (response.status === 404) {
    logger.error({
      event: 'API ERROR: getProfilesByAccountPaginated',
      responseStatusText: response.statusText,
      url,
      accountId,
      size,
      citOrigin,
      searchQuery,
      cursor,
      statusCode: response.status
    });
    throw new NotFoundError(`Profiles by account not found: ${accountId}`);
  }

  if (!response.ok) {
    logger.error({
      event: 'API ERROR: getProfilesByAccountPaginated',
      responseStatusText: response.statusText,
      url,
      accountId,
      size,
      citOrigin,
      searchQuery,
      cursor,
      statusCode: response.status
    });
    throw new HTTPError(
      response.status,
      `Failed to fetch profiles by account: ${response.statusText}`
    );
  }
  const responseData = (await response.json()) as
    | PaginatedResponse<Profile>
    | PaginatedSearchResponse<Profile>;

  return responseData;
}

export const api = {
  // Get single entry details
  getEntryDetails: async (
    accountId: string,
    entryId: string,
    citOrigin: string,
    folderId?: string
  ): Promise<Entry> => {
    const url = `${API_URL}/accounts/${accountId}/${
      folderId ? `folders/${folderId}/` : ''
    }entries/${entryId}?pn=0`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getEntryDetails',
      url,
      accountId,
      entryId,
      folderId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getEntryDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        entryId,
        folderId,
        citOrigin,
        statusCode: response.status
      });

      throw new NotFoundError(`Entry not found: ${entryId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getEntryDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        entryId,
        folderId,
        citOrigin,
        statusCode: response.status
      });

      throw new HTTPError(response.status, `Failed to fetch entry details: ${response.statusText}`);
    }

    return await response.json();
  },

  // Get search results within an account
  getAccountSearchResults: async (
    query: string,
    accountId: string,
    citOrigin: string
  ): Promise<AccountSearchResults> => {
    const url = `${API_URL}/accounts/${accountId}/search?query=${encodeURIComponent(query)}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAccountSearchResults',
      url,
      accountId,
      query,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAccountSearchResults',
        responseStatusText: response.statusText,
        url,
        accountId,
        query,
        citOrigin,
        statusCode: response.status
      });

      throw new NotFoundError(`Account search results not found: ${accountId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAccountSearchResults',
        responseStatusText: response.statusText,
        url,
        accountId,
        query,
        citOrigin,
        statusCode: response.status
      });

      throw new HTTPError(
        response.status,
        `Failed to fetch account search results: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  },

  // Get search results across all accounts
  getAllSearchResults: async (query: string, citOrigin: string): Promise<AllSearchResults> => {
    const url = `${API_URL}/search?query=${encodeURIComponent(query)}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAllSearchResults',
      url,
      query,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAllSearchResults',
        responseStatusText: response.statusText,
        url,
        query,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`All search results not found: ${query}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAllSearchResults',
        responseStatusText: response.statusText,
        url,
        query,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch all search results: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  },

  // Get folder details raw
  getFolderDetails: async (
    accountId: string,
    citOrigin: string,
    folderId?: string | undefined
  ): Promise<Folder> => {
    const url = `${API_URL}/accounts/${accountId}/folders${folderId ? `/${folderId}` : ''}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getFolderDetails',
      url,
      accountId,
      folderId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getFolderDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        folderId,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Folder details not found: ${folderId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getFolderDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        folderId,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch folder details: ${response.statusText}`
      );
    }

    return response.json();
  },

  // Get featured items
  getFeatured: async (citOrigin: string) => {
    const url = `${API_URL}/featured`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getFeatured',
      url,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getFeatured',
        responseStatusText: response.statusText,
        url,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch featured items: ${response.statusText}`
      );
    }
    return response.json() as Promise<{
      featured_accounts: Account[];
      featured_classifications: Classification[];
    }>;
  },

  getClassifications: async (citOrigin: string): Promise<Classification[]> => {
    const url = `${API_URL}/classifications`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getClassifications',
      url,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getClassifications',
        responseStatusText: response.statusText,
        url,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch classifications: ${response.statusText}`
      );
    }
    return response.json();
  },

  // Get classification entries
  getClassificationEntries: async (
    classificationId: string,
    size = ENTRIES_PER_PAGE,
    citOrigin: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry>> => {
    const params = new URLSearchParams();
    params.append('size', size.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }

    const url = `${API_URL}/classifications/${classificationId}/entries?${params}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getClassificationEntries',
      url,
      classificationId,
      size,
      citOrigin,
      cursor
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getClassificationEntries',
        responseStatusText: response.statusText,
        url,
        classificationId,
        size,
        citOrigin,
        cursor,
        statusCode: response.status
      });
      throw new NotFoundError(`Classification entries not found: ${classificationId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getClassificationEntries',
        responseStatusText: response.statusText,
        url,
        classificationId,
        size,
        citOrigin,
        cursor,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch classification entries: ${response.statusText}`
      );
    }
    return response.json();
  },

  getAccountDetails: async (accountId: string, citOrigin: string): Promise<Account> => {
    const url = `${API_URL}/accounts/${accountId}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAccountDetails',
      url,
      accountId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAccountDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Account details not found: ${accountId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAccountDetails',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch account details: ${response.statusText}`
      );
    }

    const data: Account = await response.json();

    return data;
  },

  getAccountFolders: async (accountId: string, citOrigin: string): Promise<Folder[]> => {
    const url = `${API_URL}/accounts/${accountId}/folders`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAccountFolders',
      url,
      accountId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAccountFolders',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Account folders not found: ${accountId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAccountFolders',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch folders: ${response.statusText}`);
    }
    return response.json();
  },

  getAccountProfiles: async (accountId: string, citOrigin: string): Promise<ProfileType[]> => {
    const url = `${API_URL}/accounts/${accountId}/profiles`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAccountProfiles',
      url,
      accountId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAccountProfiles',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Account profiles not found: ${accountId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAccountProfiles',
        responseStatusText: response.statusText,
        url,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch profiles: ${response.statusText}`);
    }

    const data = Object.values(await response.json()) as ProfileType[];

    return data;
  },

  getProfileTypeInfo: async (
    accountId: string,
    profileTypeURI: string,
    citOrigin: string
  ): Promise<ProfileType> => {
    const url = `${API_URL}/accounts/${accountId}/profiles/${profileTypeURI}/info`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getProfileTypeInfo',
      url,
      accountId,
      profileTypeURI,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getProfileTypeInfo',
        responseStatusText: response.statusText,
        url,
        accountId,
        profileTypeURI,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Profile type info not found: ${profileTypeURI}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getProfileTypeInfo',
        responseStatusText: response.statusText,
        url,
        accountId,
        profileTypeURI,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch profile type info: ${response.statusText}`
      );
    }

    return response.json();
  },

  getProfileDetails: async (
    profileURI: string,
    profileTypeURI: string,
    accountId: string,
    citOrigin: string
  ): Promise<Profile> => {
    const url = `${API_URL}/accounts/${accountId}/profiles/${profileTypeURI}/profiles/${profileURI}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getProfileDetails',
      url,
      profileURI,
      profileTypeURI,
      accountId,
      citOrigin
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getProfileDetails',
        responseStatusText: response.statusText,
        url,
        profileURI,
        profileTypeURI,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new NotFoundError(`Profile info not found: ${profileURI}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getProfileDetails',
        responseStatusText: response.statusText,
        url,
        profileURI,
        profileTypeURI,
        accountId,
        citOrigin,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch profile info: ${response.statusText}`);
    }
    return response.json();
  },

  getProfilesByType: async (
    accountId: string,
    type: string,
    citOrigin: string,
    size?: number,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Profile> | PaginatedSearchResponse<Profile> | undefined> => {
    const params = new URLSearchParams();
    if (size) {
      params.append('size', size.toString());
    }
    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }

    const url = `${API_URL}/accounts/${accountId}/profiles/${type}${searchQuery ? '/search' : ''}${
      params.toString() ? `?${params.toString()}` : ''
    }`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getProfilesByType',
      url,
      accountId,
      type,
      citOrigin,
      size,
      searchQuery,
      cursor
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getProfilesByType',
        responseStatusText: response.statusText,
        url,
        accountId,
        type,
        citOrigin,
        size,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new HTTPError(
        response.status,
        `Failed to fetch profiles by type: ${response.statusText}`
      );
    }

    const responseData = (await response.json()) as
      | PaginatedResponse<Profile>
      | PaginatedSearchResponse<Profile>
      | undefined;

    return responseData;
  },

  // Get entries by classification paginated
  getEntriesByClassificationPaginated: async (
    classificationId: string,
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse<Entry> | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/classifications/${classificationId}/${
      searchQuery ? 'search' : 'entries'
    }?${params.toString()}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getEntriesByClassificationPaginated',
      url,
      classificationId,
      size,
      citOrigin,
      searchQuery,
      cursor
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getEntriesByClassificationPaginated',
        responseStatusText: response.statusText,
        url,
        classificationId,
        size,
        citOrigin,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch entries by classification`);
    }
    const responseData = (await response.json()) as
      | PaginatedResponse<Entry>
      | PaginatedSearchResponse<Entry>
      | undefined;

    return responseData;
  },

  // Get entries by folder paginated
  getEntriesByFolderPaginated: async (
    accountId: string,
    folderId: string,
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Entry> | PaginatedSearchResponse<Entry> | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/accounts/${accountId}/folders/${folderId}/${
      searchQuery ? 'search' : 'entries'
    }?${params.toString()}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getEntriesByFolderPaginated',
      url,
      accountId,
      folderId,
      size,
      citOrigin,
      searchQuery,
      cursor
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getEntriesByFolderPaginated',
        responseStatusText: response.statusText,
        url,
        accountId,
        folderId,
        size,
        citOrigin,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new NotFoundError(`Entries by folder not found: ${folderId}`);
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getEntriesByFolderPaginated',
        responseStatusText: response.statusText,
        url,
        accountId,
        folderId,
        size,
        citOrigin,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch entries by folder`);
    }
    const responseData = (await response.json()) as
      | PaginatedResponse<Entry>
      | PaginatedSearchResponse<Entry>;

    return responseData;
  },
  getEntriesByAccountPaginated,
  getProfilesByAccountPaginated,

  // Get all accounts paginated
  getAccountsPaginated: async (
    size: number,
    citOrigin: string,
    searchQuery?: string,
    cursor?: string | undefined
  ): Promise<PaginatedResponse<Account> | undefined> => {
    const params = new URLSearchParams();

    if (cursor) {
      params.append('cursor', cursor);
    }
    if (searchQuery) {
      params.append('query', searchQuery);
    }
    params.append('size', size.toString());

    const url = `${API_URL}/accounts?${params.toString()}`;

    const logger = getLogger(citOrigin);
    logger.info({
      event: 'getAccountsPaginated',
      url,
      size,
      citOrigin,
      searchQuery,
      cursor
    });

    const response = await fetch(url, {
      headers: {
        'x-cit-origin': citOrigin
      }
    });

    if (response.status === 404) {
      logger.error({
        event: 'API ERROR: getAccountsPaginated',
        responseStatusText: response.statusText,
        url,
        size,
        citOrigin,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new NotFoundError('Accounts not found');
    }

    if (!response.ok) {
      logger.error({
        event: 'API ERROR: getAccountsPaginated',
        responseStatusText: response.statusText,
        url,
        size,
        citOrigin,
        searchQuery,
        cursor,
        statusCode: response.status
      });
      throw new HTTPError(response.status, `Failed to fetch accounts: ${response.statusText}`);
    }
    const responseData = (await response.json()) as PaginatedResponse<Account>;

    return responseData;
  }
};
